import {
  type SpeakingRateRuntimeControl,
  TTS_CONTROLS_VERSION,
  TTS_CONTROL_RPC_SET,
  parseTtsRuntimeControls,
} from "@/lib/ttsRuntimeControls";
import { type Participant, type Room, RoomEvent } from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseTtsRuntimeControlsOptions {
  room: Room | null;
  agentIdentity?: string;
  enabled: boolean;
}

interface SetControlResponse {
  ok?: boolean;
  version?: number;
  control?: string;
  value?: number;
  applies?: string;
  error?: string;
}

function createRequestId() {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tts-control-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function clamp(value: number, control: SpeakingRateRuntimeControl) {
  return Math.min(control.max, Math.max(control.min, value));
}

export function useTtsRuntimeControls({
  room,
  agentIdentity,
  enabled,
}: UseTtsRuntimeControlsOptions) {
  const [speakingRate, setSpeakingRate] = useState<SpeakingRateRuntimeControl | null>(null);
  const [previewValue, setPreviewValue] = useState<number | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roomRef = useRef(room);
  const agentIdentityRef = useRef(agentIdentity);
  const enabledRef = useRef(enabled);
  const speakingRateRef = useRef<SpeakingRateRuntimeControl | null>(null);
  const previewValueRef = useRef<number | null>(null);
  const pendingRef = useRef(false);

  useEffect(() => {
    roomRef.current = room;
    agentIdentityRef.current = agentIdentity;
    enabledRef.current = enabled;
  }, [agentIdentity, enabled, room]);

  useEffect(() => {
    const syncCapability = () => {
      const participant =
        enabled && room && agentIdentity ? room.remoteParticipants.get(agentIdentity) : undefined;
      const capability = parseTtsRuntimeControls(participant?.attributes);
      const nextControl = capability?.controls.speaking_rate ?? null;
      speakingRateRef.current = nextControl;
      previewValueRef.current = nextControl?.value ?? null;
      setSpeakingRate(nextControl);
      setPreviewValue(nextControl?.value ?? null);
      setError(null);
      if (!nextControl) {
        pendingRef.current = false;
        setIsPending(false);
      }
    };

    syncCapability();
    if (!room) return;

    const handleAttributesChanged = (
      _changedAttributes: Record<string, string>,
      participant: Participant
    ) => {
      if (participant.identity === agentIdentity) syncCapability();
    };
    const handleParticipantChanged = (participant: Participant) => {
      if (participant.identity === agentIdentity) syncCapability();
    };

    room.on(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
    room.on(RoomEvent.ParticipantConnected, handleParticipantChanged);
    room.on(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, handleAttributesChanged);
      room.off(RoomEvent.ParticipantConnected, handleParticipantChanged);
      room.off(RoomEvent.ParticipantDisconnected, handleParticipantChanged);
    };
  }, [agentIdentity, enabled, room]);

  const previewSpeakingRate = useCallback((value: number) => {
    const control = speakingRateRef.current;
    if (!control || !Number.isFinite(value)) return;
    const nextValue = clamp(value, control);
    previewValueRef.current = nextValue;
    setPreviewValue(nextValue);
    setError(null);
  }, []);

  const commitSpeakingRate = useCallback(async (value?: number) => {
    const activeRoom = roomRef.current;
    const destinationIdentity = agentIdentityRef.current;
    const control = speakingRateRef.current;
    if (
      !enabledRef.current ||
      !activeRoom ||
      !destinationIdentity ||
      !control ||
      pendingRef.current
    ) {
      return;
    }

    const requestedValue = clamp(value ?? previewValueRef.current ?? control.value, control);
    if (requestedValue === control.value) return;

    pendingRef.current = true;
    setIsPending(true);
    setError(null);
    try {
      const raw = await activeRoom.localParticipant.performRpc({
        destinationIdentity,
        method: TTS_CONTROL_RPC_SET,
        payload: JSON.stringify({
          version: TTS_CONTROLS_VERSION,
          control: "speaking_rate",
          value: requestedValue,
          requestId: createRequestId(),
        }),
        responseTimeout: 5_000,
      });
      const response = JSON.parse(raw) as SetControlResponse;
      if (
        !response.ok ||
        response.version !== TTS_CONTROLS_VERSION ||
        response.control !== "speaking_rate" ||
        response.applies !== "next_synthesis" ||
        typeof response.value !== "number" ||
        !Number.isFinite(response.value) ||
        response.value < control.min ||
        response.value > control.max
      ) {
        throw new Error(response.error || "invalid_response");
      }

      const nextControl = { ...control, value: response.value };
      speakingRateRef.current = nextControl;
      previewValueRef.current = response.value;
      setSpeakingRate(nextControl);
      setPreviewValue(response.value);
    } catch {
      const confirmedValue = speakingRateRef.current?.value ?? control.value;
      previewValueRef.current = confirmedValue;
      setPreviewValue(confirmedValue);
      setError("Couldn't change tutor speed. Try again.");
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, []);

  return {
    speakingRate,
    previewValue,
    isPending,
    error,
    previewSpeakingRate,
    commitSpeakingRate,
  };
}
